import React, { useEffect, useState } from "react";
import { getStudents } from "../../services/admin.services";
import { useLocation } from "react-router-dom";
import AdminComments from "../common/AdminComments";
function StudentDocs() {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const location = useLocation();
  const studentId = location.pathname.split("/").pop();

  // Define categories that correspond to your socket titles
  const categories = [
    { name: "CV Upload", title: "cv_upload" },
    { name: "Shortlisting", title: "shortlisting_of_universities" },
    { name: "SOP", title: "sop" },
    { name: "LOR", title: "lor" },
    { name: "UG Only", title: "ug_only" },
    {
      name: "University Specific SOP/Essays",
      title: "university_specific_sop",
    },
    { name: "Final Documents Upload", title: "final_documents_upload" },
    {
      name: "University Application Process",
      title: "university_application_process",
    },
    { name: "Visa Process", title: "visa_process" },
  ];

  // Map document sections to categories for chat
  const getActiveTabForChat = (sectionId) => {
    const mapping = {
      cv: "cv_upload",
      sop: "sop",
      lor: "lor",
      uni: "university_specific_sop",
      final: "final_documents_upload",
    };
    return mapping[sectionId] || null;
  };

  // Get the current active category for chat
  const getActiveCategoryForChat = () => {
    if (activeTab === "all") return null;

    const chatTitle = getActiveTabForChat(activeTab);
    return categories.find((cat) => cat.title === chatTitle);
  };

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const response = await getStudents();
        const foundStudent = response.data.data.find(
          (s) => s.student_id.toString() === studentId
        );

        // Process final_doc_urls if they exist
        if (foundStudent) {
          // Handle the case where final_doc_urls is already an object with nested structure
          if (
            foundStudent.final_doc_urls &&
            typeof foundStudent.final_doc_urls === "object" &&
            !Array.isArray(foundStudent.final_doc_urls)
          ) {
            // No need to process if we'll handle it directly in getFinalDocuments
            console.log(
              "Found final_doc_urls as object:",
              foundStudent.final_doc_urls
            );
          }

          setStudent(foundStudent);
        } else {
          setStudent(null);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [studentId]);

  const handleDownloadDocx = (url, customFileName) => {
    if (!url) {
      console.error("No URL provided for download.");
      return;
    }
    try {
      const link = document.createElement("a");
      link.href = url;
      // const baseUrl = process.env.REACT_APP_BASE_URL || "";
      // link.href = url.startsWith("/media") ? baseUrl + url : url;

      const fileName = customFileName || url.split("/").pop();
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error during file download:", error);
    }
  };

  // Helper function to check if final docs exist and have content
  const hasFinalDocuments = () => {
    if (student?.processedFinalDocs && student.processedFinalDocs.length > 0) {
      return true;
    }

    if (student?.final_doc_urls) {
      // Check if it's an array with content
      if (
        Array.isArray(student.final_doc_urls) &&
        student.final_doc_urls.length > 0
      ) {
        return true;
      }

      // Check if it's an object with properties
      if (
        typeof student.final_doc_urls === "object" &&
        Object.keys(student.final_doc_urls).length > 0
      ) {
        // Verify if any of the object properties have valid URLs
        return Object.values(student.final_doc_urls).some(
          (doc) =>
            doc &&
            (typeof doc === "string" || (typeof doc === "object" && doc.url))
        );
      }
    }

    return false;
  };

  // Get final documents in a consistent format
  const getFinalDocuments = () => {
    if (student?.processedFinalDocs) {
      return student.processedFinalDocs;
    }

    if (Array.isArray(student?.final_doc_urls)) {
      return student.final_doc_urls;
    }

    if (student?.final_doc_urls && typeof student.final_doc_urls === "object") {
      // Convert the object to an array format
      return Object.entries(student.final_doc_urls)
        .map(([key, value]) => {
          // Handle the case where value is a string (direct URL)
          if (typeof value === "string") {
            return {
              type: key,
              url: value,
              description: "",
            };
          }
          // Handle the case where value is an object with url property
          else if (typeof value === "object" && value !== null) {
            return {
              type: key,
              url: value.url || "",
              description: value.description || "",
            };
          }
          return null;
        })
        .filter((item) => item && item.url); // Filter out items without URLs
    }

    return [];
  };

  // Document type categories
  const documentSections = [
    {
      id: "cv",
      title: "Curriculum Vitae",
      icon: "📄",
      url: student?.resume_cv_url,
      fileName: student?.resume_cv_url
        ? decodeURIComponent(student.resume_cv_url.split("/").pop())
        : "",
      description: "Personal CV containing education and experience details",
    },
    {
      id: "sop",
      title: "Statement of Purpose",
      icon: "📝",
      url: student?.sop_url,
      fileName: student?.sop_url
        ? decodeURIComponent(student.sop_url.split("/").pop())
        : "",
      description: "General statement outlining academic and career goals",
    },
    {
      id: "lor",
      title: "Letters of Recommendation",
      icon: "✉️",
      isMultiple: true,
      urls: student?.lor_url || [],
      description: "Recommendation letters from professors and employers",
    },
    {
      id: "uni",
      title: "University Specific Documents",
      icon: "🏛️",
      isMultiple: true,
      subItems: [
        {
          id: "uni-sop",
          title: "University SOP",
          url: student?.university_sop_url,
          fileName: student?.university_sop_url
            ? decodeURIComponent(student.university_sop_url.split("/").pop())
            : "",
          description: "SOP tailored for specific university applications",
        },
        {
          id: "uni-essay",
          title: "University Essays",
          url: student?.university_essay_url,
          fileName: student?.university_essay_url
            ? decodeURIComponent(student.university_essay_url.split("/").pop())
            : "",
          description: "Essays prepared for university applications",
        },
      ],
    },
    {
      id: "final",
      title: "Final Documents",
      icon: "🏁",
      isMultiple: true,
      hasDocs: hasFinalDocuments(),
      urls: getFinalDocuments(),
      description: "Final application documents ready for submission",
    },
  ];

  // Filter documents based on active tab
  const filteredSections =
    activeTab === "all"
      ? documentSections
      : documentSections.filter((section) => section.id === activeTab);

  // Count documents with content
  const getDocumentCount = (section) => {
    if (section.id === "final" && hasFinalDocuments()) {
      return getFinalDocuments().length;
    } else if (section.isMultiple && section.urls) {
      return section.urls.length;
    } else if (section.subItems) {
      return section.subItems.filter((item) => item.url).length;
    } else {
      return section.url ? 1 : 0;
    }
  };

  const totalDocuments = documentSections.reduce((total, section) => {
    return total + getDocumentCount(section);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-12 w-12 bg-blue-200 rounded-full mb-4"></div>
            <div className="h-4 bg-blue-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  // Debug output (remove in production)
  console.log("Final docs structure:", student?.final_doc_urls);
  console.log("Processed final docs:", getFinalDocuments());

  return (
    <div className="bg-gray-50 min-h-screen pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 shadow-md">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold">Student Documents</h1>
          {student && (
            <p className="text-blue-100 mt-1">
              Viewing documents for {student.first_name}{" "}
              {student.last_name || ""}
            </p>
          )}
        </div>
      </div>

      {/* Document Overview */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                Documents Overview
              </h2>
              <p className="text-sm text-gray-600">
                {totalDocuments} document{totalDocuments !== 1 ? "s" : ""}{" "}
                available
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-gray-100 rounded-lg p-1 flex space-x-1 overflow-x-auto md:overflow-visible">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                    activeTab === "all"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  All Documents
                </button>
                {documentSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveTab(section.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition flex items-center ${
                      activeTab === section.id
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="mr-1">{section.icon}</span>
                    <span className="hidden md:inline-block">
                      {section.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTab === "all" ? (
          // Show all documents when "all" tab is active
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {filteredSections.map((section) => (
              <div
                key={section.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-[1.01] hover:shadow-lg"
              >
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{section.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-800">
                        {section.title}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {section.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-gray-50">
                  {section.id === "final" ? (
                    // Special handling for final documents
                    hasFinalDocuments() ? (
                      <div className="space-y-3">
                        {getFinalDocuments().map((doc, index) => {
                          // Get document type and determine appropriate icon
                          const docType = doc.type || "";

                          // Determine icon based on document type or file extension
                          let icon = "📄"; // Default document icon
                          let bgColor = "bg-blue-100";
                          let textColor = "text-blue-500";

                          // Get file extension if available
                          const fileName = doc.url
                            ? doc.url.split("/").pop()
                            : "";
                          const fileExt = fileName
                            ? fileName.split(".").pop().toLowerCase()
                            : "";

                          // Assign icons based on document type or extension
                          if (
                            docType.toLowerCase().includes("sop") ||
                            docType.toLowerCase().includes("purpose")
                          ) {
                            icon = "📝";
                            bgColor = "bg-green-100";
                            textColor = "text-green-600";
                          } else if (
                            docType.toLowerCase().includes("transcript")
                          ) {
                            icon = "🎓";
                            bgColor = "bg-purple-100";
                            textColor = "text-purple-600";
                          } else if (
                            docType.toLowerCase().includes("letter") ||
                            docType.toLowerCase().includes("lor") ||
                            docType.toLowerCase().includes("recommendation")
                          ) {
                            icon = "✉️";
                            bgColor = "bg-yellow-100";
                            textColor = "text-yellow-600";
                          } else if (docType.toLowerCase().includes("essay")) {
                            icon = "📜";
                            bgColor = "bg-indigo-100";
                            textColor = "text-indigo-600";
                          } else if (
                            docType.toLowerCase().includes("cv") ||
                            docType.toLowerCase().includes("resume")
                          ) {
                            icon = "📋";
                            bgColor = "bg-red-100";
                            textColor = "text-red-600";
                          } else if (fileExt === "pdf") {
                            icon = "📕";
                            bgColor = "bg-red-100";
                            textColor = "text-red-600";
                          } else if (["doc", "docx"].includes(fileExt)) {
                            icon = "📘";
                            bgColor = "bg-blue-100";
                            textColor = "text-blue-600";
                          } else if (["xls", "xlsx", "csv"].includes(fileExt)) {
                            icon = "📊";
                            bgColor = "bg-green-100";
                            textColor = "text-green-600";
                          } else if (
                            ["jpg", "jpeg", "png", "gif"].includes(fileExt)
                          ) {
                            icon = "🖼️";
                            bgColor = "bg-pink-100";
                            textColor = "text-pink-600";
                          }

                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                            >
                              <div className="flex items-center overflow-hidden">
                                <div
                                  className={`h-8 w-8 flex-shrink-0 rounded-full ${bgColor} flex items-center justify-center ${textColor}`}
                                >
                                  {icon}
                                </div>
                                <div className="ml-3 overflow-hidden">
                                  <p className="text-sm font-medium text-gray-700 truncate max-w-full">
                                    {docType || "Document"}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate max-w-full">
                                    {doc.url
                                      ? decodeURIComponent(
                                          doc.url.split("/").pop()
                                        )
                                      : ""}
                                  </p>
                                  {doc.description && (
                                    <p className="text-xs text-gray-400 truncate max-w-full">
                                      {doc.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => handleDownloadDocx(doc.url)}
                              >
                                <svg
                                  className="h-4 w-4 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                                Download
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 text-center rounded-md">
                        <p className="text-sm text-gray-500">
                          No final documents uploaded.
                        </p>
                      </div>
                    )
                  ) : section.isMultiple && section.urls ? (
                    section.urls.length > 0 ? (
                      <div className="space-y-3">
                        {section.urls.map((url, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200"
                          >
                            <div className="flex items-center overflow-hidden">
                              <div className="h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                                {section.icon}
                              </div>
                              <span className="ml-3 text-sm font-medium text-gray-700 truncate max-w-full">
                                {url
                                  ? decodeURIComponent(
                                      url.split(/[\\/]/).pop()
                                    ) || `Document ${index + 1}`
                                  : `Document ${index + 1}`}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              onClick={() => handleDownloadDocx(url)}
                            >
                              <svg
                                className="h-4 w-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 text-center rounded-md">
                        <p className="text-sm text-gray-500">
                          No documents uploaded.
                        </p>
                      </div>
                    )
                  ) : section.subItems ? (
                    <div className="space-y-3">
                      {section.subItems.map((item, index) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between rounded-md bg-white p-3 border ${
                            item.url
                              ? "border-gray-200"
                              : "border-gray-100 opacity-70"
                          }`}
                        >
                          <div className="flex-1 overflow-hidden">
                            <p className="text-sm font-medium text-gray-700">
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-500 truncate max-w-full">
                              {item.url
                                ? item.fileName || "Document"
                                : "Not uploaded"}
                            </p>
                          </div>
                          {item.url ? (
                            <button
                              type="button"
                              className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              onClick={() => handleDownloadDocx(item.url)}
                            >
                              <svg
                                className="h-4 w-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              Download
                            </button>
                          ) : (
                            <span className="px-3 py-1.5 text-xs text-gray-400">
                              No file
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : section.url ? (
                    <div className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200">
                      <div className="flex items-center overflow-hidden">
                        <div className="h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                          {section.icon}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700 truncate max-w-full">
                          {section.fileName || "Document"}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        onClick={() => handleDownloadDocx(section.url)}
                      >
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-4 text-center rounded-md">
                      <p className="text-sm text-gray-500">
                        No document uploaded.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Show specific section with chat when a particular tab is active
          <div className="grid grid-cols-1 ">
            {/* Document Section */}
            <div className="space-y-6">
              {filteredSections.map((section) => (
                <div
                  key={section.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{section.icon}</span>
                      <div>
                        <h3 className="font-bold text-gray-800">
                          {section.title}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-gray-50">
                    {/* Same document rendering logic as above */}
                    {section.id === "final" ? (
                      // Special handling for final documents
                      hasFinalDocuments() ? (
                        <div className="space-y-3">
                          {getFinalDocuments().map((doc, index) => {
                            // Get document type and determine appropriate icon
                            const docType = doc.type || "";

                            // Determine icon based on document type or file extension
                            let icon = "📄"; // Default document icon
                            let bgColor = "bg-blue-100";
                            let textColor = "text-blue-500";

                            // Get file extension if available
                            const fileName = doc.url
                              ? doc.url.split("/").pop()
                              : "";
                            const fileExt = fileName
                              ? fileName.split(".").pop().toLowerCase()
                              : "";

                            // Assign icons based on document type or extension
                            if (
                              docType.toLowerCase().includes("sop") ||
                              docType.toLowerCase().includes("purpose")
                            ) {
                              icon = "📝";
                              bgColor = "bg-green-100";
                              textColor = "text-green-600";
                            } else if (
                              docType.toLowerCase().includes("transcript")
                            ) {
                              icon = "🎓";
                              bgColor = "bg-purple-100";
                              textColor = "text-purple-600";
                            } else if (
                              docType.toLowerCase().includes("letter") ||
                              docType.toLowerCase().includes("lor") ||
                              docType.toLowerCase().includes("recommendation")
                            ) {
                              icon = "✉️";
                              bgColor = "bg-yellow-100";
                              textColor = "text-yellow-600";
                            } else if (
                              docType.toLowerCase().includes("essay")
                            ) {
                              icon = "📜";
                              bgColor = "bg-indigo-100";
                              textColor = "text-indigo-600";
                            } else if (
                              docType.toLowerCase().includes("cv") ||
                              docType.toLowerCase().includes("resume")
                            ) {
                              icon = "📋";
                              bgColor = "bg-red-100";
                              textColor = "text-red-600";
                            } else if (fileExt === "pdf") {
                              icon = "📕";
                              bgColor = "bg-red-100";
                              textColor = "text-red-600";
                            } else if (["doc", "docx"].includes(fileExt)) {
                              icon = "📘";
                              bgColor = "bg-blue-100";
                              textColor = "text-blue-600";
                            } else if (
                              ["xls", "xlsx", "csv"].includes(fileExt)
                            ) {
                              icon = "📊";
                              bgColor = "bg-green-100";
                              textColor = "text-green-600";
                            } else if (
                              ["jpg", "jpeg", "png", "gif"].includes(fileExt)
                            ) {
                              icon = "🖼️";
                              bgColor = "bg-pink-100";
                              textColor = "text-pink-600";
                            }

                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                              >
                                <div className="flex items-center overflow-hidden">
                                  <div
                                    className={`h-8 w-8 flex-shrink-0 rounded-full ${bgColor} flex items-center justify-center ${textColor}`}
                                  >
                                    {icon}
                                  </div>
                                  <div className="ml-3 overflow-hidden">
                                    <p className="text-sm font-medium text-gray-700 truncate max-w-full">
                                      {docType || "Document"}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate max-w-full">
                                      {doc.url
                                        ? decodeURIComponent(
                                            doc.url.split("/").pop()
                                          )
                                        : ""}
                                    </p>
                                    {doc.description && (
                                      <p className="text-xs text-gray-400 truncate max-w-full">
                                        {doc.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                  onClick={() => handleDownloadDocx(doc.url)}
                                >
                                  <svg
                                    className="h-4 w-4 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                    />
                                  </svg>
                                  Download
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-gray-50 p-4 text-center rounded-md">
                          <p className="text-sm text-gray-500">
                            No final documents uploaded.
                          </p>
                        </div>
                      )
                    ) : section.isMultiple && section.urls ? (
                      section.urls.length > 0 ? (
                        <div className="space-y-3">
                          {section.urls.map((url, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200"
                            >
                              <div className="flex items-center overflow-hidden">
                                <div className="h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                                  {section.icon}
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-700 truncate max-w-full">
                                  {url
                                    ? decodeURIComponent(
                                        url.split(/[\\/]/).pop()
                                      ) || `Document ${index + 1}`
                                    : `Document ${index + 1}`}
                                </span>
                              </div>
                              <button
                                type="button"
                                className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => handleDownloadDocx(url)}
                              >
                                <svg
                                  className="h-4 w-4 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                                Download
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 p-4 text-center rounded-md">
                          <p className="text-sm text-gray-500">
                            No documents uploaded.
                          </p>
                        </div>
                      )
                    ) : section.subItems ? (
                      <div className="space-y-3">
                        {section.subItems.map((item, index) => (
                          <div
                            key={item.id}
                            className={`flex items-center justify-between rounded-md bg-white p-3 border ${
                              item.url
                                ? "border-gray-200"
                                : "border-gray-100 opacity-70"
                            }`}
                          >
                            <div className="flex-1 overflow-hidden">
                              <p className="text-sm font-medium text-gray-700">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-500 truncate max-w-full">
                                {item.url
                                  ? item.fileName || "Document"
                                  : "Not uploaded"}
                              </p>
                            </div>
                            {item.url ? (
                              <button
                                type="button"
                                className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => handleDownloadDocx(item.url)}
                              >
                                <svg
                                  className="h-4 w-4 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                                Download
                              </button>
                            ) : (
                              <span className="px-3 py-1.5 text-xs text-gray-400">
                                No file
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : section.url ? (
                      <div className="flex items-center justify-between rounded-md bg-white p-3 border border-gray-200">
                        <div className="flex items-center overflow-hidden">
                          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                            {section.icon}
                          </div>
                          <span className="ml-3 text-sm font-medium text-gray-700 truncate max-w-full">
                            {section.fileName || "Document"}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          onClick={() => handleDownloadDocx(section.url)}
                        >
                          <svg
                            className="h-4 w-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                          Download
                        </button>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 text-center rounded-md">
                        <p className="text-sm text-gray-500">
                          No document uploaded.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Section - Only show when a specific tab is active */}
            <div className="lg:sticky lg:top-6">
              {getActiveCategoryForChat() && student && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="h-full">
                    <AdminComments
                      activeTab={getActiveCategoryForChat()}
                      selectedStudent={student}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentDocs;
